import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { organizationApi, authApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Organization.css';

export const InviteAcceptance: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, refreshOrganization } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviteInfo, setInviteInfo] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userExists, setUserExists] = useState(false);
  const [showForm, setShowForm] = useState<'login' | 'register' | 'accept' | null>(null);

  // Form states
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  const [registerForm, setRegisterForm] = useState({
    first_name: '',
    last_name: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setInviteToken(token);
      loadInviteInfo(token);
    } else {
      setError('Invalid invitation link');
    }
  }, [searchParams]);

  const loadInviteInfo = async (token: string) => {
    setIsLoading(true);
    try {
      const response = await organizationApi.getInviteInfo(token);
      setInviteInfo(response.invite);
      setUserExists(response.user_exists);
      
      if (response.user_exists) {
        setLoginForm(prev => ({ ...prev, email: response.invite.email }));
        setShowForm('login');
      } else {
        setRegisterForm(prev => ({ ...prev }));
        setShowForm('register');
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load invitation information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await authApi.login(loginForm);
      // After successful login, show accept form
      setShowForm('accept');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (registerForm.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await organizationApi.registerWithInvite(inviteToken, {
        first_name: registerForm.first_name,
        last_name: registerForm.last_name,
        password: registerForm.password
      });
      
      setSuccess('Account created and invitation accepted successfully! Redirecting...');
      
      // Refresh user and organization data
      await refreshUser();
      await refreshOrganization();
      
      // Redirect to main app after a short delay
      setTimeout(() => {
        navigate('/app');
      }, 2000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvite = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await organizationApi.acceptInvite(inviteToken);
      setSuccess('Invitation accepted successfully! Redirecting...');
      
      // Refresh user and organization data
      await refreshUser();
      await refreshOrganization();
      
      // Redirect to main app after a short delay
      setTimeout(() => {
        navigate('/app');
      }, 2000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = () => {
    navigate('/');
  };

  if (isLoading && !inviteInfo) {
    return (
      <div className="org-setup-container">
        <div className="org-setup-card">          
          <div className="org-setup-header">
            <h2>
              <span style={{color: 'var(--bg-color)'}}>todo</span>
              <span style={{color: 'var(--bg-color)', opacity: 0.8}}>bananowe.it</span>
            </h2>
            <p>Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="org-setup-container">
      <div className="org-setup-card">        
        <div className="org-setup-header">
          <h2>
            <span style={{color: 'var(--bg-color)'}}>todo</span>
            <span style={{color: 'var(--bg-color)', opacity: 0.8}}>bananowe.it</span>
          </h2>
          {inviteInfo && (
            <div>
              <p>You've been invited to join</p>
              <h3 style={{ color: 'var(--main-color)', margin: '8px 0' }}>
                {inviteInfo.organization.name}
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--sub-color)' }}>
                by {inviteInfo.invited_by.first_name} {inviteInfo.invited_by.last_name}
              </p>
            </div>
          )}
        </div>

        <div className="org-setup-form">
          {error && (
            <div className="org-error">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
            </div>
          )}

          {!error && !success && showForm === 'login' && (
            <form onSubmit={handleLogin}>
              <h3 style={{ color: 'var(--text-color)', marginBottom: '16px' }}>
                Please log in to accept the invitation
              </h3>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  required
                  disabled={isLoading}
                  readOnly
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                  disabled={isLoading}
                  placeholder="Enter your password"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button 
                  type="submit"
                  className="org-submit-btn"
                  disabled={isLoading}
                  style={{ flex: 1 }}
                >
                  {isLoading ? 'Logging in...' : 'Log In & Accept'}
                </button>
                
                <button 
                  type="button"
                  onClick={handleDecline}
                  className="org-submit-btn"
                  disabled={isLoading}
                  style={{ 
                    flex: 1, 
                    background: 'var(--sub-color)', 
                    color: 'var(--bg-color)' 
                  }}
                >
                  Decline
                </button>
              </div>
            </form>
          )}

          {!error && !success && showForm === 'register' && (
            <form onSubmit={handleRegister}>
              <h3 style={{ color: 'var(--text-color)', marginBottom: '16px' }}>
                Create your account to join the organization
              </h3>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={inviteInfo?.email || ''}
                  readOnly
                  disabled
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>First Name</label>
                  <input
                    type="text"
                    value={registerForm.first_name}
                    onChange={(e) => setRegisterForm({ ...registerForm, first_name: e.target.value })}
                    required
                    disabled={isLoading}
                    placeholder="Enter your first name"
                  />
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={registerForm.last_name}
                    onChange={(e) => setRegisterForm({ ...registerForm, last_name: e.target.value })}
                    required
                    disabled={isLoading}
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                  required
                  disabled={isLoading}
                  placeholder="Choose a password (min. 6 characters)"
                  minLength={6}
                />
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={registerForm.confirmPassword}
                  onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                  required
                  disabled={isLoading}
                  placeholder="Confirm your password"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button 
                  type="submit"
                  className="org-submit-btn"
                  disabled={isLoading}
                  style={{ flex: 1 }}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account & Join'}
                </button>
                
                <button 
                  type="button"
                  onClick={handleDecline}
                  className="org-submit-btn"
                  disabled={isLoading}
                  style={{ 
                    flex: 1, 
                    background: 'var(--sub-color)', 
                    color: 'var(--bg-color)' 
                  }}
                >
                  Decline
                </button>
              </div>
            </form>
          )}

          {!error && !success && showForm === 'accept' && (
            <>
              <p style={{ color: 'var(--text-color)', textAlign: 'center', marginBottom: '24px' }}>
                Welcome back! Would you like to accept this invitation to join <strong>{inviteInfo?.organization.name}</strong>?
              </p>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button 
                  onClick={handleAcceptInvite}
                  className="org-submit-btn"
                  disabled={isLoading}
                  style={{ flex: 1 }}
                >
                  {isLoading ? 'Accepting...' : 'Accept Invitation'}
                </button>
                
                <button 
                  onClick={handleDecline}
                  className="org-submit-btn"
                  disabled={isLoading}
                  style={{ 
                    flex: 1, 
                    background: 'var(--sub-color)', 
                    color: 'var(--bg-color)' 
                  }}
                >
                  Decline
                </button>
              </div>
            </>
          )}

          {error && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
              <button 
                onClick={() => navigate('/')}
                className="org-submit-btn"
                style={{ 
                  background: 'var(--sub-color)', 
                  color: 'var(--bg-color)' 
                }}
              >
                Go to App
              </button>
            </div>
          )}
        </div>

        <div className="org-setup-info">
          <h3>What happens when you join?</h3>
          <ul>
            <li>You'll become a {inviteInfo?.role || 'member'} of the organization</li>
            <li>You'll have access to shared tasks and projects</li>
            <li>You can collaborate with other team members</li>
            <li>Your permissions will be set according to your role</li>
          </ul>
        </div>
      </div>
    </div>
  );
}; 